import { useEffect, useState } from "react";
import { Circle, Polygon, Popup, useMapEvents, useMap } from "react-leaflet";

interface GeofenceMapLayerProps {
    geofences: any[];
    drawingMode: "circle" | "polygon" | null;
    onGeofenceDrawn: (geofence: {
        type: "circle" | "polygon";
        center?: { lat: number; lng: number };
        radius?: number;
        coordinates?: { lat: number; lng: number }[];
    }) => void;
    onCancelDraw: () => void;
}

function haversineDistance(lat1: number, lng1: number, lat2: number, lng2: number): number {
    const R = 6371000;
    const toRad = (deg: number) => (deg * Math.PI) / 180;
    const dLat = toRad(lat2 - lat1);
    const dLng = toRad(lng2 - lng1);
    const a =
        Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) * Math.sin(dLng / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
}

const DrawingLayer = ({
    drawingMode,
    onGeofenceDrawn,
    onCancelDraw,
}: {
    drawingMode: "circle" | "polygon";
    onGeofenceDrawn: GeofenceMapLayerProps["onGeofenceDrawn"];
    onCancelDraw: () => void;
}) => {
    const map = useMap();
    const [circleCenter, setCircleCenter] = useState<{ lat: number; lng: number } | null>(null);
    const [previewRadius, setPreviewRadius] = useState<number>(0);
    const [polygonPoints, setPolygonPoints] = useState<{ lat: number; lng: number }[]>([]);
    const [mousePos, setMousePos] = useState<{ lat: number; lng: number } | null>(null);

    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === "Escape") {
                onCancelDraw();
            }
        };
        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [onCancelDraw]);

    useEffect(() => {
        map.getContainer().style.cursor = "crosshair";
        return () => {
            map.getContainer().style.cursor = "";
        };
    }, [map]);

    useMapEvents({
        click(e) {
            if (drawingMode === "circle") {
                if (!circleCenter) {
                    setCircleCenter({ lat: e.latlng.lat, lng: e.latlng.lng });
                } else {
                    const radius = haversineDistance(circleCenter.lat, circleCenter.lng, e.latlng.lat, e.latlng.lng);
                    onGeofenceDrawn({ type: "circle", center: circleCenter, radius });
                }
            } else if (drawingMode === "polygon") {
                setPolygonPoints((prev) => [...prev, { lat: e.latlng.lat, lng: e.latlng.lng }]);
            }
        },
        dblclick(e) {
            if (drawingMode === "polygon" && polygonPoints.length >= 3) {
                e.originalEvent.preventDefault();
                onGeofenceDrawn({ type: "polygon", coordinates: polygonPoints });
            }
        },
        mousemove(e) {
            if (drawingMode === "circle" && circleCenter) {
                setPreviewRadius(haversineDistance(circleCenter.lat, circleCenter.lng, e.latlng.lat, e.latlng.lng));
            }
            if (drawingMode === "polygon") {
                setMousePos({ lat: e.latlng.lat, lng: e.latlng.lng });
            }
        },
    });

    return (
        <>
            {drawingMode === "circle" && circleCenter && (
                <Circle
                    center={[circleCenter.lat, circleCenter.lng]}
                    radius={previewRadius}
                    pathOptions={{ color: "#f59e0b", fillColor: "rgba(245,158,11,0.15)", dashArray: "6,6" }}
                />
            )}

            {drawingMode === "polygon" && polygonPoints.length > 0 && (
                <Polygon
                    positions={[
                        ...polygonPoints.map((p) => [p.lat, p.lng] as [number, number]),
                        ...(mousePos ? [[mousePos.lat, mousePos.lng] as [number, number]] : []),
                    ]}
                    pathOptions={{ color: "#f59e0b", fillColor: "rgba(245,158,11,0.15)", dashArray: "6,6" }}
                />
            )}

            <div
                className="absolute bottom-4 left-1/2 -translate-x-1/2 z-[1000] bg-black/70 text-white text-xs px-3 py-1.5 rounded-full pointer-events-none"
            >
                {drawingMode === "circle"
                    ? circleCenter
                        ? "Click to set radius"
                        : "Click to set center"
                    : `Click to add points${polygonPoints.length >= 3 ? ", double-click to finish" : ""}`}
                {" · Press Escape to cancel"}
            </div>
        </>
    );
};

const GeofenceMapLayer = ({ geofences, drawingMode, onGeofenceDrawn, onCancelDraw }: GeofenceMapLayerProps) => {
    return (
        <>
            {(geofences || []).map((geofence: any) => {
                const isActive = geofence.active !== false;
                const fillColor = isActive ? "rgba(245,158,11,0.15)" : "rgba(156,163,175,0.15)";
                const borderColor = isActive ? "#f59e0b" : "#9ca3af";

                if (geofence.type === "circle" && geofence.center && geofence.radius) {
                    return (
                        <Circle
                            key={geofence.id}
                            center={[geofence.center.lat, geofence.center.lng]}
                            radius={geofence.radius}
                            pathOptions={{ color: borderColor, fillColor, fillOpacity: 0.3, weight: 2 }}
                        >
                            <Popup>{geofence.name}</Popup>
                        </Circle>
                    );
                }

                if (geofence.type === "polygon" && geofence.coordinates) {
                    return (
                        <Polygon
                            key={geofence.id}
                            positions={geofence.coordinates.map((c: { lat: number; lng: number }) => [c.lat, c.lng])}
                            pathOptions={{ color: borderColor, fillColor, fillOpacity: 0.3, weight: 2 }}
                        >
                            <Popup>{geofence.name}</Popup>
                        </Polygon>
                    );
                }

                return null;
            })}

            {drawingMode && (
                <DrawingLayer
                    drawingMode={drawingMode}
                    onGeofenceDrawn={onGeofenceDrawn}
                    onCancelDraw={onCancelDraw}
                />
            )}
        </>
    );
};

export default GeofenceMapLayer;
